import { Form, FormInstance, Radio, Tooltip } from 'antd';
import AlertSerialPrice from 'components/alerts/AlertSerialPrice';
import PackageItemTitleContainer from 'components/forms/PackageFormModal/PackageItemTitleContainer';
import React, { useEffect, useState } from 'react';
import { useGetInstructionOptionsQuery } from 'services/instructionService';
import { BASE_QUERY_OPTIONS } from 'utils/constants';
import { checkIfSerialCheaper } from 'utils/formFuncs';
import { BaseSearchOption, DiagnosticSearchOption, isInstanceOfDiagnosticOption } from 'utils/types/InstructionOrderTypes';
import { DiagnosticEstimateItemNew } from 'utils/types/estimateTypesNew';
import OrderFrequency from 'views/visit/TreatmentSheet/OrderFrequency';
import EditLineButton from '../forms/PackageFormModal/EditLineButton';
import { HiddenInput } from '../forms/fields/HiddenInput';
import EstimateFrequencyFormItem from './EstimateMedicationForm/EstimateFrequencyFormItem';
import EstimateInstructionFormItem from './EstimateMedicationForm/EstimateInstructionFormItem';
import EstimateStartEndTimeFormItems from './EstimateMedicationForm/EstimateStartEndTimeFormItems';
import { EstimateFormInstance } from './index';

const getDiagnosticOption = (
    diagnostic: DiagnosticEstimateItemNew,
    searchOptions?: BaseSearchOption[],
): DiagnosticSearchOption | undefined => {
    if (!searchOptions) {
        return undefined;
    }

    return searchOptions?.find((option) => {
        return option.id === diagnostic.diagnostic_id && isInstanceOfDiagnosticOption(option);
    }) as DiagnosticSearchOption | undefined;
};

interface EstimateDiagnosticsFormProps {
    diagnostic: DiagnosticEstimateItemNew;
    form: FormInstance<EstimateFormInstance>;
    index: number;
    hiddenSectionClass?: string;
    setActiveLineItem: (item: string) => void;
    activeLineItem: string;
    typeId: string;
}

const EstimateDiagnosticsForm: React.FC<EstimateDiagnosticsFormProps> = ({
    diagnostic,
    form,
    hiddenSectionClass,
    index,
    activeLineItem,
    typeId,
    setActiveLineItem,
}) => {
    const { data: searchOptions } = useGetInstructionOptionsQuery(null, BASE_QUERY_OPTIONS);
    const diagnosticOption = getDiagnosticOption(diagnostic, searchOptions);

    const [hasError, setHasError] = useState(false);
    const lineId = `${typeId}-${diagnostic.name}-${index}`;
    const isEditing = activeLineItem === lineId || hasError;
    const itemName = typeId === 'D' ? 'diagnostics' : 'refLabs';

    const diagFields = [
        [itemName, index, 'frequency'],
        [itemName, index, 'end_time'],
        [itemName, index, 'start_time'],
        [itemName, index, 'instruction'],
    ];
    const diagsErrorArray = form.getFieldsError(diagFields);

    const [isSerialCheaper, setIsSerialCheaper] = useState(diagnostic.serial);

    const setSerial = (changes: { [key in string]: string }) => {
        if (!diagnosticOption) {
            return;
        }
        const fieldsValue = form.getFieldsValue();
        const diagnosticFields = {
            ...fieldsValue[itemName][index],
            ...changes,
        };
        const startTime = (diagnosticFields?.start_time ?? fieldsValue.start_time)?.unix();
        const endTime = (diagnosticFields?.end_time ?? fieldsValue.end_time)?.unix();

        const serial = checkIfSerialCheaper(diagnosticOption, diagnosticFields.frequency, startTime, endTime);
        const unit_cost_cents = serial ? diagnosticOption?.price_cents_serial ?? 0 : diagnosticOption?.cents ?? 0;

        setIsSerialCheaper(serial);

        form.setFieldsValue({
            ...fieldsValue,
            [itemName]: fieldsValue[itemName].map((diag: any, indx: number) => {
                return indx === index ? { ...diag, ...changes, serial, unit_cost_cents } : diag;
            }),
        });
    };

    const handleShouldUpdate = (prev: EstimateFormInstance, curr: EstimateFormInstance) => {
        return (
            prev[itemName][index].frequency !== curr[itemName][index].frequency ||
            prev[itemName][index].start_time !== curr[itemName][index].start_time ||
            prev[itemName][index].end_time !== curr[itemName][index].end_time ||
            prev[itemName][index].action !== curr[itemName][index].action
        );
    };

    useEffect(() => {
        if (isEditing) {
            form.getFieldInstance([itemName, index, 'frequency'])?.focus();
        }
    }, [isEditing, form, index]);

    useEffect(() => {
        if (diagsErrorArray.some((e) => e.errors.length)) {
            setHasError(true);
        } else {
            setHasError(false);
        }
    }, [form, diagsErrorArray]);

    useEffect(() => {
        setSerial({});
    }, []);

    return (
        <Form.Item shouldUpdate={handleShouldUpdate} noStyle>
            {({ getFieldValue }) => {
                const initialError = !(getFieldValue([itemName, index, 'frequency']) ?? diagnostic.frequency);
                const serialName = isSerialCheaper
                    ? `${diagnostic.name} - Serial ${diagnosticOption?.serial_hours} Hours`
                    : diagnostic.name;

                return (
                    <div
                        className={`grid-table__row ${hiddenSectionClass}`}
                        onClick={() => {
                            if (!isEditing && getFieldValue([itemName, index, 'action']) === 0) setActiveLineItem(lineId);
                        }}
                    >
                        <div style={{ display: 'none' }}>
                            <HiddenInput fieldName={[itemName, index, 'id']} initialValue={diagnostic.diagnostic_id} />
                            <HiddenInput fieldName={[itemName, index, 'name']} initialValue={diagnostic.name} />

                            <HiddenInput fieldName={[itemName, index, 'type_id']} initialValue={diagnostic.type_id} />
                            <HiddenInput fieldName={[itemName, index, 'instruction_id']} initialValue={diagnostic.instruction_id} />
                            <HiddenInput fieldName={[itemName, index, 'is_prn']} initialValue={diagnostic.is_prn} />
                            <HiddenInput fieldName={[itemName, index, 'prn_condition']} initialValue={diagnostic.prn_condition} />
                            <HiddenInput fieldName={[itemName, index, 'priority']} initialValue={diagnostic.priority} />
                            <HiddenInput fieldName={[itemName, index, 'serial']} initialValue={isSerialCheaper} />
                            <HiddenInput fieldName={[itemName, index, 'serial_hours']} initialValue={diagnostic.serial_hours} />
                            <HiddenInput fieldName={[itemName, index, 'is_free']} initialValue={diagnostic.is_free} />
                        </div>

                        <div className='grid-table__sticky-column'>
                            <PackageItemTitleContainer no_charge={diagnostic.is_free} checked>
                                <Tooltip title={serialName.length > 45 ? serialName : null}>
                                    <span className='treatment-sheet-side-column__title'>{serialName}</span>
                                </Tooltip>
                            </PackageItemTitleContainer>

                            <div className='diagnostic-data'>
                                <OrderFrequency>
                                    {getFieldValue([itemName, index, 'frequency']) ?? diagnostic.frequency}
                                </OrderFrequency>
                            </div>
                        </div>

                        <div className='package-modal__mobile-header'>
                            <span style={{ color: 'var(--veg-red)' }}>* </span>
                            Transfer Options
                        </div>

                        <div className='package-medication-dose__inputs'>
                            <Form.Item name={[itemName, index, 'action']} initialValue={0}>
                                <Radio.Group buttonStyle='solid'>
                                    <Radio.Button value={0}>Transfer</Radio.Button>
                                    <Radio.Button value={1}>Exclude</Radio.Button>
                                    <Radio.Button value={2}>Save for Later</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </div>

                        <div className='empty-element' />
                        <div className='empty-element' />

                        <div className='package-modal__mobile-header'>
                            <span style={{ color: 'var(--veg-red)' }}>* </span>
                            Frequency
                        </div>
                        <EstimateFrequencyFormItem
                            formFieldName={[itemName, index, 'frequency']}
                            initialValue={diagnostic.frequency}
                            isEditing={isEditing}
                            formFieldValue={form.getFieldValue([itemName, index, 'frequency']) ?? diagnostic.frequency}
                            disabled={getFieldValue([itemName, index, 'action']) !== 0 || typeId !== 'D'}
                            onFormChange={(changes) => {
                                setSerial(changes);
                            }}
                        />

                        <EstimateStartEndTimeFormItems
                            endTimeFormFieldName={[itemName, index, 'end_time']}
                            endTimeFormFieldValue={getFieldValue([itemName, index, 'end_time'])}
                            isEditing={isEditing}
                            startTimeFormFieldName={[itemName, index, 'start_time']}
                            startTimeFormFieldValue={getFieldValue([itemName, index, 'start_time'])}
                            disabled={getFieldValue([itemName, index, 'action']) !== 0}
                        />

                        <EstimateInstructionFormItem
                            formFieldName={[itemName, index, 'instruction']}
                            initialValue={null}
                            isEditing={isEditing}
                            formFieldValue={form.getFieldValue([itemName, index, 'instruction'])}
                            disabled={getFieldValue([itemName, index, 'action']) !== 0}
                        />
                        {isEditing && isSerialCheaper && (
                            <div
                                className={`serial-alert-column-transfer ${
                                    !isEditing || !isSerialCheaper ? 'serial-alert-column-transfer--hide' : ''
                                }`}
                            >
                                <AlertSerialPrice serialHours={diagnosticOption?.serial_hours ?? 0} />
                            </div>
                        )}

                        <EditLineButton
                            disabled={isEditing}
                            hasError={hasError || initialError}
                            onClick={() => {
                                if (!hasError) {
                                    setActiveLineItem(lineId);
                                }
                            }}
                        />
                    </div>
                );
            }}
        </Form.Item>
    );
};

export default EstimateDiagnosticsForm;
